import React, { useEffect, useRef, useState, useCallback } from 'react';
import { post } from '../helpers/api_helper';
import { POST_GET_POSTCODE } from '../helpers/url_helper';

export default function Header(props) {
    const selectRef = useRef(null);
    const debounceRef = useRef(null);

    const [search, setSearch] = useState(props.address || '');
    const [activeOption, setActiveOption] = useState(-1);
    const [showOptions, setShowOptions] = useState(false);
    const [suggestion, setSuggestion] = useState([]);

    useEffect(() => {
        const listener = (event) => handleClickOutside(event);
        document.addEventListener('click', listener);
        return () => document.removeEventListener('click', listener);
    }, []);

    // Send postcode to backend - backend handles everything (DB check + external API + DB save)
    const fetchAddresses = async (postcode) => {
        const cleanPostcode = postcode.trim().replace(/\s+/g, ' ');

        try {
            const response = await post(POST_GET_POSTCODE, { postcode: cleanPostcode });
            if (response && response.data && response.data.length > 0) {
                console.log('Addresses returned for:', cleanPostcode, response.data);
                return response.data;
            }
        } catch (err) {
            console.log('Postcode fetch error:', err);
        }

        return [];
    };

    // Format a raw address object into a display string
    const formatAddress = (addr) => {
        return [addr.line_1, addr.line_2, addr.line_3, addr.post_town, addr.postcode]
            .filter(Boolean)
            .join(', ');
    };

    // Debounced search
    const debouncedSearch = useCallback((searchValue) => {
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }
        debounceRef.current = setTimeout(async () => {
            if (searchValue.trim().length >= 3) {
                const addresses = await fetchAddresses(searchValue);
                const formatted = addresses.map(addr => ({
                    address: formatAddress(addr),
                    latitude: addr.latitude,
                    longitude: addr.longitude,
                    raw: addr,
                }));
                setSuggestion(formatted);
                setShowOptions(formatted.length > 0);
            } else {
                setSuggestion([]);
                setShowOptions(false);
            }
        }, 400);
    }, []);

    const onChange = (e) => {
        const searchValue = e.currentTarget.value;

        if (!searchValue) {
            props.callBack({
                latitude: '',
                longitude: '',
                address: '',
            });
            setSuggestion([]);
            setShowOptions(false);
        }

        setSearch(searchValue);
        setActiveOption(-1);
        debouncedSearch(searchValue);
    };

    const onKeyDown = (e) => {
        if (e.keyCode === 13) {
            e.preventDefault();
            if (showOptions && activeOption >= 0) {
                selectAddress(suggestion[activeOption]);
                setShowOptions(false);
                setActiveOption(-1);
            }
        } else if (e.keyCode === 38) {
            if (activeOption <= 0) return;
            setActiveOption(activeOption - 1);
        } else if (e.keyCode === 40) {
            if (activeOption >= suggestion.length - 1) return;
            setActiveOption(activeOption + 1);
        }
    };

    const selectAddress = (selected) => {
        setSearch(selected.address);
        setShowOptions(false);
        setActiveOption(-1);
        console.log('Selected address:', selected.raw);
        props.callBack({
            latitude: selected.latitude || '',
            longitude: selected.longitude || '',
            address: selected.address,
        });
    };

    const handleClickOutside = (event) => {
        if (selectRef.current && !selectRef.current.contains(event.target)) {
            setShowOptions(false);
        }
    };

    return (
        <div className='search_box' ref={selectRef}>
            <input
                type="text"
                onChange={onChange}
                onKeyDown={onKeyDown}
                value={search}
                className="input form-control text-capitalize"
                placeholder={props.placeholder}
            />
            {showOptions && suggestion.length > 0 && (
                <div className="suggestion">
                    <ul className="options">
                        {suggestion.map((item, index) => {
                            const className = index === activeOption
                                ? 'active text-capitalize'
                                : 'text-capitalize';
                            return (
                                <li
                                    className={className}
                                    key={index}
                                    onClick={() => selectAddress(item)}
                                >
                                    {item.address}
                                </li>
                            );
                        })}
                    </ul>
                </div>
            )}
        </div>
    );
}
